import { upperFirst, filter } from 'lodash';
import { useFeature } from '@optimizely/react-sdk';
import { useLearningLifecyclesContext } from '@src/context/LearningLifecyclesContext';
import { useMemberPlanContext } from '@src/context/MemberPlanContext';
import { useMemberTraitsContext } from '@src/context/MemberTraitsContext';
import { MilestoneKey } from '@src/context/MilestoneContext';
import { useProfileCompletionContext } from '@src/context/ProfileCompletionContext';

/**
 * Builds an array of LLC milestones for the member using Member Traits and Plan data.
 * @packageDocumentation
 */
const useMemberMilestones = () => {
  const { memberPlan } = useMemberPlanContext();
  const { memberTraits } = useMemberTraitsContext();
  const { isProfileComplete, showBachelorsWarning } = useProfileCompletionContext();
  const { currentLearningLifecycle } = useLearningLifecyclesContext();
  const [isEnabled] = useFeature('home_funding_milestone');

  const milestones: MilestoneKey[] = [];

  const defaultDecision = () => {
    if (memberPlan?.hasTuitionAssistance) {
      if (memberTraits?.hasQuizRecommendations) {
        milestones.push(MilestoneKey.PreApplicantCatalogWithRecs);
      } else {
        milestones.push(MilestoneKey.PreApplicantProgramQuiz, MilestoneKey.PreApplicantCatalog);
      }
    } else if (memberPlan?.hasSponsorship) {
      milestones.push(MilestoneKey.PreApplicantRequestSponsorship);
    } else if (memberPlan?.hasTuitionReimbursement) {
      milestones.push(MilestoneKey.PreApplicantRequestReimbursement);
    } else {
      milestones.push(MilestoneKey.PreApplicantNoPlanData);
    }
    if (isEnabled && memberPlan && !memberTraits?.hasApplications) {
      milestones.splice(1, 0, MilestoneKey.PreApplicantFindFunding);
    }
  };

  if (!isProfileComplete) {
    milestones.push(MilestoneKey.CompleteYourSignup);
  } else if (memberPlan?.hasNoCoverage) {
    milestones.push(MilestoneKey.PreApplicantNoPlanData);
  } else if (showBachelorsWarning) {
    milestones.push(MilestoneKey.PreApplicantBachelorsWarning);
  } else if (currentLearningLifecycle) {
    let currentMilestones = [...(currentLearningLifecycle.milestones || [])];

    if (!memberPlan?.hasCareerAccelerator) {
      currentMilestones = filter(
        currentMilestones,
        (milestone) =>
          milestone !== 'learnerCareerAccelerator' && milestone !== 'completerCareerAccelerator'
      );
    }

    if (!memberPlan?.hasBooksAndFees) {
      currentMilestones = filter(
        currentMilestones,
        (milestone) => milestone !== MilestoneKey.LearnerBooksAndFeesReimbursement
      );
    }

    currentMilestones?.forEach((milestone) => {
      const adjustedMilestone: string = upperFirst(milestone);
      if (adjustedMilestone && MilestoneKey[adjustedMilestone]) {
        milestones.push(MilestoneKey[adjustedMilestone]);
      }
      return milestones;
    });

    // Pre-Applicant Stage (LLC Phase 1)
  } else if (memberTraits?.isTransitionMember) {
    if (memberTraits?.transitionProgram?.fundingLevel === 'TA') {
      milestones.push(MilestoneKey.TransitionStartProgramApplication);
    } else if (memberTraits?.transitionProgram?.fundingLevel === 'DPP') {
      milestones.push(MilestoneKey.TransitionRequestSponsorship);
    } else if (memberTraits?.transitionProgram?.fundingLevel === 'TR') {
      milestones.push(MilestoneKey.TransitionRequestReimbursement);
    } else {
      defaultDecision();
    }
  } else {
    defaultDecision();
  }

  return milestones;
};

export default useMemberMilestones;
